import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Typography, Alert, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import AdminService from '@/services/AdminService';

const { Paragraph } = Typography;

const SESSION_KEY = 'session';

// AdminService exporta la clase, no una instancia: mismo patrón que el resto
// de páginas del panel (`const admin = new AdminService()`).
const admin = new AdminService();

/**
 * Formulario bloqueante de cambio de contraseña.
 *
 * Se muestra cuando el login devolvió `must_change_password: true`. No se puede
 * cerrar: sin Escape, sin clic fuera, sin botón de cancelar. La única salida es
 * definir una contraseña nueva o cerrar sesión.
 *
 * Pide la contraseña actual además de la nueva porque el servidor la exige: el
 * cambio se hace sobre el usuario de la sesión y se verifica contra su hash, de
 * modo que un token robado no basta para apropiarse de la cuenta.
 */
const ForcePasswordChange: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return;
      const session = JSON.parse(raw);
      if (session && session.must_change_password) setVisible(true);
    } catch {
      // Sesión ilegible: no se bloquea la aplicación por esto.
    }
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      await admin.changeOwnPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });

      // Se limpia el flag en la sesión guardada para que no reaparezca al recargar.
      try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (raw) {
          const session = JSON.parse(raw);
          session.must_change_password = false;
          localStorage.setItem(SESSION_KEY, JSON.stringify(session));
        }
      } catch {
        // Si no se puede reescribir, el flag vuelve a salir en el próximo ingreso.
      }

      message.success('Contraseña actualizada. Tus otras sesiones se cerraron.');
      setVisible(false);
      form.resetFields();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || 'No se pudo cambiar la contraseña.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem(SESSION_KEY);
    window.location.href = '/#/login';
  };

  return (
    <Modal
      open={visible}
      title="Debes definir una contraseña nueva"
      closable={false}
      keyboard={false}
      maskClosable={false}
      footer={null}
      destroyOnClose={false}
      width={460}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Un administrador solicitó el cambio."
        description="No puedes continuar hasta definir una contraseña nueva."
      />

      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        Mínimo 10 caracteres, y distinta de la actual. Al guardar se cerrarán tus
        demás sesiones.
      </Paragraph>

      <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
        <Form.Item
          name="currentPassword"
          label="Contraseña actual"
          rules={[{ required: true, message: 'Escribe tu contraseña actual' }]}
        >
          <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="Nueva contraseña"
          rules={[
            { required: true, message: 'Escribe la contraseña nueva' },
            { min: 10, message: 'Debe tener al menos 10 caracteres' },
          ]}
          hasFeedback
        >
          <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Repite la nueva contraseña"
          dependencies={['newPassword']}
          hasFeedback
          rules={[
            { required: true, message: 'Repite la contraseña nueva' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Las contraseñas no coinciden'));
              },
            }),
          ]}
        >
          <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" block loading={submitting}>
            Guardar contraseña
          </Button>
          <Button type="link" block onClick={handleLogout} style={{ marginTop: 8 }}>
            Cerrar sesión
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ForcePasswordChange;
